import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin';
import { Submission } from '../models/submission.model';
import { storagePrefix } from '../utils/storage-key';
import { AuthService } from './auth.service';
import { SentryService } from './sentry.service';
import { IdbStorageService, IDB_STORE_QUEUE } from './idb-storage.service';
import { ToastService } from '../../shared/services/toast.service';
import { UPQUEUE_PERSIST_FAILED_MSG } from '../constants/app.messages';

/**
 * Unscoped fallback storage key used when the user's entity ID is not yet known.
 * Written by `queue()` before login completes.
 * Migrated to the user-scoped key by `restoreQueue()` on the first successful auth.
 */
const PREAUTH_QUEUE_KEY = 'upqueue_pending';

/**
 * Identity of a submission that survives a storage round-trip (object
 * references do not). Used to dedupe on restore and to remove sent items from
 * a stored queue that is no longer loaded in memory.
 *
 * @param item - Queued submission.
 * @returns `"{timestamp}|{jobId}|{type}"`.
 */
function submissionKey(item: Submission): string {
  return `${item.timestamp}|${item.jobId}|${item.type}`;
}

/**
 * Returns `list` without the sent items and with rejected items marked.
 *
 * @param list        - Queue snapshot.
 * @param isSent      - Whether an item was confirmed by the server.
 * @param rejectionOf - HTTP status if the item was permanently rejected.
 * @param at          - Rejection timestamp (ISO 8601).
 * @returns A new list; untouched items keep their object identity.
 */
function settle(
  list: Submission[],
  isSent: (i: Submission) => boolean,
  rejectionOf: (i: Submission) => number | undefined,
  at: string,
): Submission[] {
  return list
    .filter(i => !isSent(i))
    .map(i => {
      const status = rejectionOf(i);
      return status === undefined ? i : { ...i, rejected: { status, at } };
    });
}

/**
 * Builds the user-scoped queue storage key.
 *
 * @param serverId - Process server entity ID.
 * @returns `"{hashedEntityId}_queue_submissions"`.
 */
function scopedQueueKey(serverId: number): string {
  return `${storagePrefix(serverId)}_queue_submissions`;
}

/**
 * Manages the offline submission queue for attempt logs and signed affidavits.
 *
 * @remarks
 * **Storage policy:** submissions are ALWAYS written to persistent storage immediately.
 * - Before login (serverId unknown): written to `'upqueue_pending'` (unscoped fallback).
 * - After login: written to `'{hashedEntityId}_queue_submissions'` (user-scoped key).
 * - On first login after pre-auth queuing: `restoreQueue()` migrates `'upqueue_pending'`
 *   into the scoped key and removes the fallback — no data is ever lost to an app crash.
 *
 * **Backing store is IndexedDB, not `capacitor-secure-storage-plugin` (2026-08-05).**
 * The key names above are unchanged; only the medium moved. That plugin's web
 * fallback is `localStorage` (`btoa()`-encoded, ~5 MB per origin shared with
 * every other cache), which a single base64 evidence photo could exhaust —
 * `QuotaExceededError`, confirmed live in UAT. See {@link IdbStorageService}
 * for the full rationale. {@link readQueue} transparently migrates a queue
 * left behind by a pre-2026-08-05 build, and {@link writeQueue} still falls
 * back to the plugin if IndexedDB is unusable, so no submission is stranded
 * either way.
 *
 * **Survives logout (2026-10-09, product direction).** "Log out" / "Log out
 * anyway" must not destroy unsent field work, so `AuthService.clearAllStorage()`
 * leaves `{hashedEntityId}_queue_submissions` (and the whole `IDB_STORE_QUEUE`
 * store) in place. This service's logout `effect()` only clears the
 * *in-memory* `pendingSet`; `restoreQueue()` reloads the user-scoped key when
 * the SAME user signs in again, and auto-sync then uploads it. A different
 * user signing in on the same device never sees it — the key is scoped by the
 * hashed entity ID.
 *
 * A transmission still in flight at logout removes its sent items from the
 * owner's stored queue via {@link removeItems}' `ownerId` path, so they are
 * not re-sent after the next login.
 *
 * Auto-retry on reconnect is handled by {@link NetworkService}, which calls
 * {@link UpqueueAdapter.transmitAll} when `isOnline` becomes `true` and
 * `pendingCount` is non-zero.
 */
@Injectable({ providedIn: 'root' })
export class UpqueueService {
  private readonly authService  = inject(AuthService);
  private readonly sentry       = inject(SentryService);
  private readonly idb          = inject(IdbStorageService);
  private readonly toastService = inject(ToastService);

  /** All submissions awaiting upload (persisted across app restarts). */
  readonly pendingSet = signal<Submission[]>([]);

  /** Count of submissions awaiting upload — drives the Upqueue tab badge. */
  readonly pendingCount = computed(() => this.pendingSet().length);

  /** Read-only snapshot of all queued submissions, oldest first. */
  readonly items = this.pendingSet.asReadonly();

  /**
   * Process server entity ID of the currently authenticated user.
   * `null` until the auth effect fires and parses the JWT.
   * When `null`, `queue()` appends to `PREAUTH_QUEUE_KEY` via {@link appendToFallbackKey}.
   */
  private currentServerId: number | null = null;

  /**
   * Serialises concurrent pre-auth writes to the fallback key.
   * Each `appendToFallbackKey` call chains onto this Promise so reads and
   * writes to `PREAUTH_QUEUE_KEY` are strictly sequential.
   */
  private fallbackWriteChain: Promise<void> = Promise.resolve();

  constructor() {
    // On login: capture serverId, merge any in-memory pre-auth items, restore scoped queue.
    // On logout: clear the in-memory signal only — the persisted queue is kept
    // so the same user's unsent work uploads after they sign in again (see this
    // class's own doc comment above).
    effect(() => {
      if (this.authService.isAuthenticated()) {
        this.currentServerId = this.authService.token()?.user?.entityId ?? null;
        void this.restoreQueue();
      } else {
        this.currentServerId = null;
        this.pendingSet.set([]);
      }
    });
  }

  // ─── Public API ───────────────────────────────────────────────────────────

  /**
   * Adds a submission to the queue and immediately persists it to storage.
   *
   * @remarks
   * Always writes to storage so no data is lost if the app is killed before upload.
   * - When authenticated: writes the full `pendingSet` to the user-scoped key.
   * - When not yet authenticated: **appends** the new item to the fallback key
   *   `'upqueue_pending'` without overwriting items already stored there
   *   (e.g. crash survivors from a previous run). `restoreQueue()` migrates
   *   the fallback key to the scoped key on the next login.
   *
   * A storage failure (see {@link persistOrWarn}) does NOT discard the
   * submission — it stays in the in-memory queue so an immediate online
   * transmit can still succeed.
   *
   * @param submission - The completed attempt or affidavit to queue.
   */
  queue(submission: Submission): void {
    const updated = [...this.pendingSet(), submission];
    this.pendingSet.set(updated);
    if (this.currentServerId) {
      void this.persistOrWarn(
        scopedQueueKey(this.currentServerId),
        updated,
        'queue',
      );
    } else {
      void this.appendToFallbackKey(submission);
    }
  }

  /**
   * Alias for {@link queue} — adds a submission and persists it.
   * Provided so consumers can use `enqueue()` consistently with the
   * {@link removeItems} / {@link items} API.
   *
   * @param submission - The completed attempt or affidavit to queue.
   */
  enqueue(submission: Submission): void {
    this.queue(submission);
  }

  /**
   * Removes the given submissions using referential equality and immediately
   * persists the remainder to storage.
   *
   * @remarks
   * Always writes to storage — never leaves the on-disk state stale.
   * Uses the fallback key when the server entity ID is not yet known.
   *
   * When `ownerId` is given and is NOT the currently loaded user (the user
   * logged out — or switched — while a transmission was in flight), the
   * in-memory queue no longer holds these items. They are instead removed
   * from the owner's stored queue by {@link submissionKey}, so the sent items
   * are not resurrected and re-sent at that user's next login.
   *
   * @param toRemove - Exact object references obtained from {@link items}.
   * @param ownerId  - Entity ID of the user the items were transmitted for.
   */
  removeItems(toRemove: Submission[], ownerId?: number): void {
    this.applyResults(toRemove, [], ownerId);
  }

  /**
   * Applies one transmission's outcome in a single write: removes the items
   * the server accepted and marks the permanently rejected ones (never
   * deleting them — 2026-10-09, product direction).
   *
   * @remarks
   * A rejected item stays in the queue with a {@link Submission.rejected}
   * marker: it is still counted by {@link pendingCount} (so logout stays
   * blocked), is skipped by automatic uploads, and is only re-sent by a manual
   * "Transmit all". Re-rejection refreshes the marker; a later success removes
   * the item like any other.
   *
   * Matching follows {@link removeItems}: by reference for the loaded queue;
   * by {@link submissionKey} when `ownerId` is not the loaded user (logout
   * mid-transmit), against that owner's stored queue.
   *
   * @param sent       - Items the server confirmed (exact references from {@link items}).
   * @param rejections - Items the server permanently rejected, with the HTTP status.
   * @param ownerId    - Entity ID of the user the items were transmitted for.
   */
  applyResults(
    sent: Submission[],
    rejections: Array<{ item: Submission; status: number }>,
    ownerId?: number,
  ): void {
    const at = new Date().toISOString();
    if (ownerId != null && ownerId !== this.currentServerId) {
      void this.applyToStoredQueue(ownerId, sent, rejections, at);
      return;
    }
    const sentSet  = new Set(sent);
    const rejected = new Map(rejections.map(r => [r.item, r.status] as const));
    const updated  = settle(this.pendingSet(), i => sentSet.has(i), i => rejected.get(i), at);
    this.pendingSet.set(updated);
    const key = this.currentServerId ? scopedQueueKey(this.currentServerId) : PREAUTH_QUEUE_KEY;
    void this.persistOrWarn(key, updated, 'removeItems');
  }

  // ─── Private helpers ─────────────────────────────────────────────────────

  /**
   * {@link applyResults} for a user's stored queue that is not loaded in
   * memory — matches by {@link submissionKey} since references don't survive
   * a storage round-trip.
   *
   * @param ownerId    - Entity ID whose stored queue to update.
   * @param sent       - Items the server confirmed.
   * @param rejections - Items the server permanently rejected.
   * @param at         - Rejection timestamp.
   */
  private async applyToStoredQueue(
    ownerId: number,
    sent: Submission[],
    rejections: Array<{ item: Submission; status: number }>,
    at: string,
  ): Promise<void> {
    const key      = scopedQueueKey(ownerId);
    const sentKeys = new Set(sent.map(submissionKey));
    const rejected = new Map(rejections.map(r => [submissionKey(r.item), r.status] as const));
    const stored   = await this.readQueue(key);
    const updated  = settle(
      stored,
      i => sentKeys.has(submissionKey(i)),
      i => rejected.get(submissionKey(i)),
      at,
    );
    await this.persistOrWarn(key, updated, 'removeItems');
  }

  /**
   * Persists a queue snapshot, preferring IndexedDB.
   *
   * @remarks
   * Falls back to `capacitor-secure-storage-plugin` when IndexedDB is
   * unavailable or its write fails — some private-browsing modes disable
   * IndexedDB entirely, and losing the queue there would be a regression
   * against the very thing this move is meant to protect. The fallback write
   * is the pre-2026-08-05 behaviour verbatim (`JSON.stringify` into the same
   * key), so {@link readQueue} finds it either way.
   *
   * Rejects only when BOTH backends fail — {@link persistOrWarn} treats that
   * as the real, user-visible failure.
   *
   * @param key - Storage key (`{hash}_queue_submissions` or the pre-auth key).
   * @param items - Full queue snapshot.
   */
  private async writeQueue(key: string, items: Submission[]): Promise<void> {
    try {
      await this.idb.set(IDB_STORE_QUEUE, key, items);
      return;
    } catch (idbErr) {
      this.sentry.captureException(idbErr, {
        reason: 'upqueue-idb-write-failed-falling-back',
        key,
        queueLength: items.length,
      });
    }
    await SecureStoragePlugin.set({ key, value: JSON.stringify(items) });
  }

  /**
   * Reads a queue snapshot, migrating a legacy `capacitor-secure-storage-plugin`
   * entry into IndexedDB on first encounter.
   *
   * @remarks
   * **The migration is why this method exists rather than a bare
   * `idb.get()`.** A build before 2026-08-05 wrote the queue through the
   * plugin; on the first launch after upgrading, IndexedDB is empty while
   * real unsent field submissions still sit in the old key. Reading only
   * IndexedDB would silently strand them. The legacy entry is removed only
   * after the IndexedDB write succeeds, so an interrupted migration retries
   * on the next launch instead of losing data.
   *
   * @param key - Storage key to read.
   * @returns The stored submissions, or `[]` when neither backend has any.
   */
  private async readQueue(key: string): Promise<Submission[]> {
    const fromIdb = await this.idb.get<Submission[]>(IDB_STORE_QUEUE, key);
    if (Array.isArray(fromIdb)) return fromIdb;

    let legacy: Submission[] = [];
    try {
      const { value } = await SecureStoragePlugin.get({ key });
      legacy = JSON.parse(value) as Submission[];
    } catch {
      return []; // Absent in both backends — first run, or already drained.
    }
    if (!Array.isArray(legacy) || legacy.length === 0) return [];

    try {
      await this.idb.set(IDB_STORE_QUEUE, key, legacy);
      await SecureStoragePlugin.remove({ key });
    } catch (err) {
      // Migration failed — the legacy copy is deliberately left in place so
      // the next launch can retry. The items are still returned and usable.
      this.sentry.captureException(err, { reason: 'upqueue-idb-migration-failed', key });
    }
    return legacy;
  }

  /**
   * Writes the given queue snapshot to storage, reporting any failure instead
   * of letting it surface as an unhandled promise rejection.
   *
   * @remarks
   * **Why this exists (2026-08-05):** every write here used to be a bare
   * `void SecureStoragePlugin.set(...)`. On web/PWA that plugin's fallback is
   * `localStorage` (`cap_sec_`-prefixed, `btoa()`-encoded — see
   * `PHOTO_MAX_EDGE_PX` in `photo.service.ts` for the size maths), so a
   * base64 photo payload could throw `QuotaExceededError`. With no `catch`,
   * that became an unhandled rejection: Sentry recorded it, but the officer
   * saw a normal success state while `pendingSet` had already been updated —
   * i.e. the submission looked queued and was silently never persisted.
   * Confirmed live from a UAT Sentry trace (2026-07-31): a single photo on a
   * *final* attempt, the completion type that closes a job out.
   *
   * **The in-memory queue is deliberately NOT rolled back on failure.**
   * Rolling back would delete the only copy of the officer's photos, notes
   * and GPS outright, and would also prevent
   * `AttemptLogComponent.handleSubmit()`'s immediate
   * `UpqueueAdapter.transmitAll()` from sending it — which, when online, is
   * the very thing most likely to save the submission. Keeping it in memory
   * preserves that chance; the toast tells the officer the durability
   * guarantee is gone so they can act (upload now) rather than close the app.
   *
   * The warning toast is shown for the `'queue'` call site only. A
   * `'removeItems'` write is strictly *shrinking* the stored value, so it
   * cannot realistically hit a quota limit, and its failure mode (a
   * successfully-transmitted item resurrected from disk on the next launch,
   * risking a duplicate submission) is not something the officer can act on.
   * `'restoreQueue'` re-writes the merged queue during login, when there is
   * no submission in progress and nothing actionable to surface. All three
   * are captured to Sentry.
   *
   * @param key - The resolved storage key to write.
   * @param items - The full queue snapshot to persist.
   * @param origin - Which public method requested the write.
   */
  private async persistOrWarn(
    key: string,
    items: Submission[],
    origin: 'queue' | 'removeItems' | 'restoreQueue',
  ): Promise<void> {
    try {
      await this.writeQueue(key, items);
    } catch (err) {
      this.sentry.captureException(err, {
        reason:      'upqueue-persist-failed',
        origin,
        key,
        queueLength: items.length,
      });
      if (origin === 'queue') {
        void this.toastService.errorUntilDismissed(UPQUEUE_PERSIST_FAILED_MSG);
      }
    }
  }

  /**
   * Appends a single submission to the fallback key, reading the existing list first.
   *
   * @remarks
   * Calls are chained on {@link fallbackWriteChain} so rapid back-to-back `queue()`
   * calls are serialised — no concurrent read-modify-write races.
   * This ensures crash-survivor items already in the fallback key are never
   * overwritten by a new item's write.
   *
   * @param submission - The newly queued item to append.
   */
  private appendToFallbackKey(submission: Submission): void {
    this.fallbackWriteChain = this.fallbackWriteChain.then(async () => {
      const existing = await this.readQueue(PREAUTH_QUEUE_KEY);
      await this.persistOrWarn(PREAUTH_QUEUE_KEY, [...existing, submission], 'queue');
    });
  }

  /**
   * Restores the persisted queue on login, migrates the pre-auth fallback key,
   * and merges any remaining in-memory items.
   *
   * @remarks
   * Three sources are merged (oldest-first) and **deduplicated** by composite key
   * `(timestamp|jobId|type)` to handle the timing race where
   * {@link appendToFallbackKey} may not have completed before this method runs:
   * 1. User-scoped key `'{hashedId}_queue_submissions'` — submissions from a prior session.
   * 2. Fallback key `'upqueue_pending'` — submissions persisted before the JWT was parsed.
   * 3. In-memory `pendingSet` — items held since app start, guaranteed present
   *    even if the fallback write is still in-flight.
   *
   * After merging: the scoped key is written with the full deduplicated list, and
   * the fallback key is removed so it is never read twice.
   */
  private async restoreQueue(): Promise<void> {
    if (!this.currentServerId) return;
    const scopedKey = scopedQueueKey(this.currentServerId);

    // Both reads go through readQueue(), which also migrates a queue left in
    // the legacy secure-storage key by a pre-2026-08-05 build.
    const stored        = await this.readQueue(scopedKey);
    const preAuthStored = await this.readQueue(PREAUTH_QUEUE_KEY);

    // In-memory items: guaranteed to contain any items queued via queue() even if
    // appendToFallbackKey() hasn't completed its storage write yet.
    const preAuthMemory = this.pendingSet();

    // Deduplicate by composite key to prevent double-counting items that appear in
    // both the fallback key and pendingSet due to the async write timing race.
    const seen    = new Set<string>();
    const merged: Submission[] = [];
    for (const item of [...stored, ...preAuthStored, ...preAuthMemory]) {
      const k = submissionKey(item);
      if (!seen.has(k)) { seen.add(k); merged.push(item); }
    }

    if (merged.length === 0) return;

    this.pendingSet.set(merged);
    await this.persistOrWarn(scopedKey, merged, 'restoreQueue');

    if (preAuthStored.length > 0) {
      // Remove from BOTH backends — readQueue() may have just migrated this
      // key into IndexedDB, and a stale legacy copy would be re-read next login.
      await this.idb.remove(IDB_STORE_QUEUE, PREAUTH_QUEUE_KEY);
      try { await SecureStoragePlugin.remove({ key: PREAUTH_QUEUE_KEY }); } catch { /* already gone */ }
    }
  }
}
